import { Menu, Transition } from "@headlessui/react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { DateRangePicker as RangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useRangePicker } from "../store/use-range-picker-model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarXmark } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

interface IDateRangePicker {
  children: React.ReactNode;
  onChange: (e: Record<string, any>) => void;
}

const DateRangePicker = ({ children, onChange }: IDateRangePicker) => {
  const { initialValues, onCloseRangePicker } = useRangePicker();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{
    top: number;
    left?: number;
    right?: number;
  }>({ top: 0, left: 0 });

  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
    new: true,
  });

  // Anchor to whichever edge of the trigger leaves room: a trigger in the right
  // half pins the panel's right edge to it, one in the left half pins its left.
  // Anchoring by the far edge means the panel always opens back toward the
  // middle of the viewport, so it never needs its own width measured.
  const positionPanel = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 16;
    const top = rect.bottom + 10;
    const anchorRight = rect.left + rect.width / 2 > window.innerWidth / 2;

    const next = anchorRight
      ? { top, right: Math.max(margin, window.innerWidth - rect.right) }
      : { top, left: Math.max(margin, rect.left) };

    setPanelPos((prev) =>
      prev.top === next.top &&
      prev.left === next.left &&
      prev.right === next.right
        ? prev
        : next,
    );
  }, []);

  useLayoutEffect(() => {
    if (!initialValues.isOpen) return;
    positionPanel();
  }, [initialValues.isOpen, positionPanel]);

  useEffect(() => {
    if (!initialValues.isOpen) return;
    window.addEventListener("resize", positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    return () => {
      window.removeEventListener("resize", positionPanel);
      window.removeEventListener("scroll", positionPanel, true);
    };
  }, [initialValues.isOpen, positionPanel]);

  const handleSelect = (e) => {
    const dataSelection = {
      startDate: moment(e.selection?.startDate).format("YYYY-MM-DD"),
      endDate:
        e.selection?.startDate === e.selection?.endDate
          ? null
          : moment(e.selection?.endDate).format("YYYY-MM-DD"),
      key: "selection",
    };

    onChange(dataSelection);
    setDateRange({ ...e.selection, new: false });
  };

  const resetFilter = () => {
    const dataSelection = {
      startDate: null,
      endDate: null,
      key: "selection",
    };

    onChange(dataSelection);
    setDateRange({
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
      new: true,
    });
  };

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 640;

  const panel =
    typeof document !== "undefined"
      ? createPortal(
          <Transition
            as="div"
            className="fixed inset-0 z-[100000] pointer-events-none"
            enter="transition ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={initialValues.isOpen}
          >
            <div
              className="absolute inset-0 sm:hidden bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm pointer-events-auto"
              onClick={onCloseRangePicker}
            />
            <div
              className="hidden sm:block absolute inset-0 pointer-events-auto"
              onClick={onCloseRangePicker}
            />

            <div
              style={
                isMobile
                  ? undefined
                  : {
                      top: panelPos.top,
                      left: panelPos.left,
                      right: panelPos.right,
                    }
              }
              className="
                absolute
                left-1/2 sm:left-auto top-1/2 sm:top-auto
                -translate-x-1/2 -translate-y-1/2 sm:translate-x-0 sm:translate-y-0
                w-[min(95vw,360px)] sm:w-auto sm:max-w-[calc(100vw-32px)]
                p-3 sm:p-4
                rounded-2xl border border-edge shadow-overlay
                flex items-center justify-center
                bg-surface-elevated
                animate-fade-in
                overflow-hidden
                pointer-events-auto
              "
              onClick={(e) => e.stopPropagation()}
            >
              <div className="max-h-[80vh] overflow-y-auto custom-scrollbar">
                <RangePicker
                  ranges={[dateRange]}
                  onChange={handleSelect}
                  rangeColors={["#6366F1"]}
                  showMonthAndYearPickers={true}
                />
              </div>
            </div>
          </Transition>,
          document.body
        )
      : null;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div className="flex items-center gap-2" ref={triggerRef}>
        <Menu.Button data-bs-toggle="dropdown" className="cursor-pointer">
          {children}
        </Menu.Button>
        {!dateRange.new && (
          <div
            onClick={resetFilter}
            className="cursor-pointer px-4 rounded-full appearance-none text-[14px] flex items-center justify-center h-10 bg-surface text-light-gray border border-edge shadow-card outline-none hover:border-edge-strong hover:text-text-primary transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faCalendarXmark} />
          </div>
        )}
      </div>

      {panel}
    </Menu>
  );
};

export default DateRangePicker;
